import { getFilterGroupsFromConfigurations } from "@/services/configurations.service";
import { type Configuration } from "@/types/campaign";

// Returns true if all configurations in the group are disabled.
export function isSegmentDisabled(configurationsGroup: Configuration[]) {
  return configurationsGroup.every((c) => !c.enabled || c.deleted);
}

export function getTimelineDistribution(configurations: Configuration[]) {
  const distribution = configurations.every((c) => c.idealLeadShare === 100 / 3)
    ? "even"
    : "linear";

  return distribution;
}

export function getConfigurationFilterCount(config: Configuration) {
  let count = 0;
  if ((config.contactConfiguration?.personas?.length ?? 0) > 0) count++;
  const filterGroups = getFilterGroupsFromConfigurations([config]);
  count += filterGroups.size;
  return count;
}

export function qualityRequiresContact({
  configurations,
  quality,
}: {
  configurations: Configuration[];
  quality: number;
}) {
  const groupConfigurations = configurations.filter(
    (c) => c.quality === quality,
  );
  const enabledConfigurations = groupConfigurations.filter(
    (c) => c.enabled && !c.deleted,
  );
  if (enabledConfigurations.length === 0) return false;
  return enabledConfigurations.every((c) => c.contactConfiguration !== null);
}

export function getContactConfigurationFromquality({
  configurations,
  quality,
}: {
  configurations: Configuration[];
  quality: number;
}) {
  const configuration = configurations.find(
    (c) => c.quality === quality && c.contactConfiguration !== null,
  );

  return configuration?.enabled && !configuration?.deleted
    ? configuration?.contactConfiguration
    : undefined;
}

export function hasContactConfiguration(configurations: Configuration[]) {
  return configurations.some((c) => c.contactConfiguration !== null);
}

export function getContactConfiguration(configurations: Configuration[] = []) {
  const active = configurations.filter((c) => c.enabled && !c.deleted);
  const withRoles = active.find(
    (c) => (c.contactConfiguration?.personas?.length ?? 0) > 0,
  );

  return withRoles?.contactConfiguration;
}

export const detectCurrentStrategy = (configurations: Configuration[]) => {
  const strategies = [
    {
      label: "round_robin",
      title: "Balanceret",
      condition: (configs: Configuration[]) => {
        const goldConfigs = configs.filter((c) => c.quality === 1);
        const silverConfigs = configs.filter((c) => c.quality === 2);
        return (
          goldConfigs.some((c) => c.idealLeadShare === 50) ||
          silverConfigs.some((c) => c.idealLeadShare === 0) ||
          goldConfigs.some((c) => c.idealLeadShare === 33)
        );
      },
    },
    {
      label: "gold_first",
      title: "Offensiv",
      condition: (configs: Configuration[]) => {
        const goldConfigs = configs.filter((c) => c.quality === 1);
        const silverConfigs = configs.filter((c) => c.quality === 2);
        return (
          goldConfigs.some((c) => c.priority === 1) &&
          silverConfigs.some((c) => c.priority === 2)
        );
      },
    },
    {
      label: "soft_start",
      title: "Defensiv",
      condition: (configs: Configuration[]) => {
        const goldConfigs = configs.filter((c) => c.quality === 1);
        const silverConfigs = configs.filter((c) => c.quality === 2);
        return (
          silverConfigs.some((c) => c.priority === 1) &&
          goldConfigs.some((c) => c.priority === 2)
        );
      },
    },
  ];

  const matchedStrategy = strategies.find((strategy) =>
    strategy.condition(configurations),
  );
  return matchedStrategy || { label: "gold_first", title: "Guldet først" };
};

export const colors = {
  Guld: {
    primary: "#F9CF64",
    secondary: "#FFE299",
  },
  Sølv: {
    primary: "#ABABAB",
    secondary: "#E8E8E8",
  },
  Bronze: {
    primary: "#B08E24",
    secondary: "#C0A03E",
  },
};
